import SeoWrapper from 'components/Seo/SeoWrapper';
import VehicleListWrapper from 'components/Vehicle/VehicleList/VehicleListWrapper';
import { defaultPagination } from 'constants/defaultPagination';
import { Locale } from 'enums/locale';
import { VehiclePriceUnit } from 'enums/vehiclePriceUnit';
import { checkAuthentication } from 'guards/checkAuthentication';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import { getSearchMetadata, getSeoData } from 'lib/seo/seoData';
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  NextPage,
} from 'next';
import { CategoryTypes, linkService } from 'services/LinkService';
import {
  localizationService,
  SlugTranslations,
} from 'services/LocalizationService';
import { locationService } from 'services/LocationService';
import { searchService } from 'services/SearchService';
import {
  isSearchResponseExperience,
  PaginatedVehicleWithBreadcrumbs,
  PaginatedExperienceWithBreadcrumbs,
} from 'types/search.type';
import { HelmetSettings, SeoProps } from 'types/seo.type';
import { DropdownOption } from 'types/types';
import { VehicleSummary } from 'types/vehicle.type';
import getT from 'next-translate/getT';
import BImage from 'components/common/BImage';
import TranslatedLink from 'components/common/TranslatedLink';
import { useLocale } from 'hooks/useLocale';

export const getServerSideProps: GetServerSideProps = checkAuthentication(
  async (ctx: GetServerSidePropsContext) => {
    const searchQueries = searchService.getSearchFilterQueries(ctx.query);
    const filterQueries = searchService.mapFilterStateToServerDto(
      searchService.getFilterQueryParams(ctx.resolvedUrl.split('?')[1])
    );

    const slug = ctx.params?.category as string;
    const locale = ctx.locale as Locale;

    const slugTranslations: SlugTranslations = {
      category: {
        en: '',
        tr: '',
      },
      location: {
        en: '',
        tr: '',
      },
    };

    const extraxtedUrl = linkService.extractLocationFromCategoryUrl(slug);

    let category: CategoryTypes | null = null;
    let location: string | null = null;
    let breadcrumbCategory: DropdownOption<string> = { label: '', value: '' };

    if (extraxtedUrl) {
      const matchResult = extraxtedUrl.category;
      const translatableCategoryObject =
        linkService.categoryLinkTranslations.find(
          (a) => a[locale] === matchResult
        );

      if (translatableCategoryObject) {
        slugTranslations.category = { ...translatableCategoryObject };
        const trMatchResult = translatableCategoryObject.tr;
        category = linkService.categoryMap[trMatchResult];
        breadcrumbCategory = {
          label:
            linkService.categoryLinkTitleTranslations[trMatchResult][locale],
          value: translatableCategoryObject[locale],
        };
      } else {
        return {
          notFound: true,
        };
      }
      location = extraxtedUrl.location;
    } else {
      return {
        notFound: true,
      };
    }

    if (location) {
      const locationTranslations = locationService.getLocationLink(location);
      if (locationTranslations) {
        slugTranslations.location = { ...locationTranslations };
      }
    }

    let searchPromise;
    let searchCheapestPricePromise;

    if (category === 'EXPERIENCE') {
      searchPromise = searchService.getCompleteExperienceListWithFilterSSR(
        defaultPagination,
        {
          ...(location ? { locationSlug: location } : {}),
          ...searchQueries,
        },
        ctx
      );
    } else {
      const vehicleCategory = linkService.getRealVehicleCategory(category);

      searchPromise = searchService.getCompleteVehicleListWithFilterSSR(
        defaultPagination,
        {
          ...filterQueries,
          ...(location ? { locationSlugs: [location] } : {}),
          ...(vehicleCategory ? { vehicleTypes: [vehicleCategory] } : {}),
        },
        searchQueries,
        ctx
      );
      searchCheapestPricePromise = searchService.getCheapestPriceForSeoSSR(
        ctx,
        {
          location,
          vehicleType: vehicleCategory,
        }
      );
    }

    const path = '/' + (locale === Locale.TR ? '' : 'en/') + slug; // burda process gerekebilir
    const url = `https://www.teknevia.com${path}`;

    const seoPath = path;

    const seoDataFromWpPromise = getSeoData(seoPath, locale, path);

    const promises: [
      typeof searchPromise,
      typeof seoDataFromWpPromise,
      typeof searchCheapestPricePromise
    ] = [searchPromise, seoDataFromWpPromise, searchCheapestPricePromise];

    const [response, seoDataFromWp, cheapestSeoPrice] = await Promise.all(
      promises
    );

    if (location && !response.location) {
      return {
        notFound: true,
      };
    }

    const { content } = seoDataFromWp;

    const title = (() => {
      let title = '';
      if (response.location) {
        const loc =
          response.location[
            `${response.location?.matchType}Name` as keyof typeof response.location
          ];
        title += loc + ' ';
      }
      title += breadcrumbCategory.label;
      return title;
    })();

    const t = await getT(locale, 'seo');

    let data;
    if (isSearchResponseExperience(response)) {
      data = response.experiences;
    } else {
      data = response.vehicles;
    }

    const helmetSettings = getSearchMetadata(
      ctx.locale as Locale,
      url,
      t('category.title', { category: title }),
      t('category.description', {
        category: title,
        vehicleCount: data?.totalElements || 0,
        categoryKey: breadcrumbCategory.label
          .split(' ')
          .slice(0, -1)
          .join(' ')
          .toLowerCase(),
        priceUnit: t(
          `category.priceUnit.${
            cheapestSeoPrice?.price?.unit || VehiclePriceUnit.HOUR
          }`
        ),
        count: cheapestSeoPrice?.price?.amount || 0,
      })
    ) as HelmetSettings;

    if (slugTranslations) {
      localizationService.slugTranslations = { ...slugTranslations };
    }

    return {
      props: {
        paginatedVehicleWithBreadcrumbs: response,
        slugTranslations,
        helmetSettings,
        content,
        breadcrumbCategory,
        category,
        title,
      },
    };
  }
);

interface RentalBoatsByTypeProps extends SeoProps {
  paginatedVehicleWithBreadcrumbs:
    | PaginatedVehicleWithBreadcrumbs
    | PaginatedExperienceWithBreadcrumbs;
  slugTranslations: SlugTranslations;
  category: CategoryTypes;
  breadcrumbCategory: DropdownOption<string>;
  cheapestSeoPrice: VehicleSummary;
  title: string;
}

const RentalBoatsByType: NextPage<RentalBoatsByTypeProps> = ({
  paginatedVehicleWithBreadcrumbs,
  slugTranslations,
  category,
  helmetSettings,
  content,
  breadcrumbCategory,
  title,
}) => {
  localizationService.slugTranslations = { ...slugTranslations };
  const { locale } = useLocale();

  const { defaultBreadcrumbs, generateCategoryLinks } = useBreadcrumbs();

  const breadcrumbItems = (() => {
    return [
      ...defaultBreadcrumbs,
      ...generateCategoryLinks(
        breadcrumbCategory,
        paginatedVehicleWithBreadcrumbs.location
      ),
    ];
  })();

  const isExperience = isSearchResponseExperience(
    paginatedVehicleWithBreadcrumbs
  );

  const bannerLink = linkService.getCategoryLinkOption(
    null,
    locale,
    'EXPERIENCE'
  );

  const bannerImageLink = `https://cdn.teknevia.com/storefront/common/experience-turkish-night-banner-${locale}.png`;

  return (
    <SeoWrapper helmetSettings={helmetSettings} content={content}>
      {!isExperience ? (
        <TranslatedLink href={bannerLink.value}>
          <a>
            <div className="relative mx-auto mb-6 block w-full md:mb-0 md:w-[28rem]">
              <BImage
                src={bannerImageLink}
                layout="responsive"
                width={171}
                height={50}
              />
            </div>
          </a>
        </TranslatedLink>
      ) : null}
      <VehicleListWrapper
        title={title}
        apiVehicles={paginatedVehicleWithBreadcrumbs}
        breadcrumbItems={breadcrumbItems}
        defaultVehicleType={category}
      />
    </SeoWrapper>
  );
};

export default RentalBoatsByType;
